import React, { useState, useMemo } from 'react';
import { X, Loader2, AlertTriangle, Mail, Clock } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDuration = (mins) => {
  if (mins <= 0) return '';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h > 0 && m > 0) return `${h}h ${m}min`;
  if (h > 0) return `${h}h`;
  return `${m}min`;
};

export default function CancelOrderModal({ order, token, isAdmin, onClose, onCancelled }) {
  const { t } = useTranslation('orders');
  // Admin invariant: shared component used by both panels — only customer
  // uses i18n; admin keeps English.
  const tt = (key, en) => (isAdmin ? en : t(key, { defaultValue: en }));
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  // For users: the deadline must have passed before cancellation is allowed.
  const tooEarly = useMemo(() => {
    if (isAdmin) return null;
    const deadline = order.priority_deadline ? new Date(order.priority_deadline).getTime() : 0;
    if (!deadline) return { reason: 'no-deadline', minsLeft: 0 };
    const remaining = deadline - Date.now();
    if (remaining <= 0) return null;
    return { reason: 'before-deadline', minsLeft: Math.ceil(remaining / 60000), at: order.priority_deadline };
  }, [isAdmin, order.priority_deadline]);

  const submit = async () => {
    setErr(null);
    if (isAdmin && !reason.trim()) {
      setErr('Please provide a reason — it will be sent to the customer by email.');
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${API_URL}/api/orders/${order.id}/cancel`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: isAdmin ? reason.trim() : '' }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setErr(d.detail || `HTTP ${r.status}`);
        return;
      }
      if (onCancelled) onCancelled();
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  // ── User attempting to cancel before deadline → friendly informational dialog ──
  if (tooEarly) {
    const niceTime = tooEarly.at
      ? new Date(tooEarly.at).toLocaleString(undefined, { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' })
      : null;
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="cancel-too-early-modal">
        <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
          <div className="flex items-center justify-between p-4 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-bold text-slate-800">{tt('cancel_modal.not_available_title', 'Cancellation not available yet')}</h3>
                <p className="text-xs text-slate-500">{tt('cancel_modal.order_label', 'Order')} {order.order_number || order.id.slice(0, 8)}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded">
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>
          <div className="p-5 space-y-3 text-sm text-slate-700 leading-relaxed">
            <p>
              {isAdmin ? (
                <>Your order is still being processed within the priority window you selected.
                Cancellation becomes available <strong>only after the priority deadline expires</strong>.</>
              ) : (
                <Trans
                  i18nKey="cancel_modal.body"
                  ns="orders"
                  components={{ strong: <strong /> }}
                  defaults="Your order is still being processed within the priority window you selected. Cancellation becomes available <strong>only after the priority deadline expires</strong>."
                />
              )}
            </p>
            {niceTime && (
              <div className="px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2 text-amber-900">
                <Clock className="w-4 h-4 flex-shrink-0" />
                <span>
                  {tt('cancel_modal.available_after', 'Available to cancel after')} <strong>{niceTime}</strong>
                  {tooEarly.minsLeft > 0 && <> · {tt('cancel_modal.in', 'in')} <strong>{fmtDuration(tooEarly.minsLeft)}</strong></>}
                </span>
              </div>
            )}
            <p className="text-slate-600">
              {tt('cancel_modal.thanks', 'Thanks for your patience — most orders are completed well within the chosen priority time.')}
            </p>
          </div>
          <div className="flex gap-2 p-4 border-t border-slate-200">
            <button
              onClick={onClose}
              data-testid="too-early-ok-btn"
              className="ml-auto px-5 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg font-medium hover:shadow-md"
            >
              {tt('cancel_modal.ok_wait', "OK, I'll wait")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Confirm dialog (admin always; user only when deadline passed) ──
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="cancel-order-modal">
      <div className="bg-white rounded-xl max-w-lg w-full shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-rose-100 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-rose-600" />
            </div>
            <div>
              <h3 className="font-bold text-slate-800">{tt('cancel_modal.confirm_title', 'Cancel order?')}</h3>
              <p className="text-xs text-slate-500">{tt('cancel_modal.order_label', 'Order')} {order.order_number || order.id.slice(0, 8)}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-slate-700">
            {isAdmin
              ? 'Are you sure you want to cancel this order? This action cannot be undone. The reserved amount will be refunded and the customer will be notified by email.'
              : tt('cancel_modal.body_customer', 'Are you sure you want to cancel this order? This action cannot be undone. The reserved amount will be refunded back to your balance.')}
          </p>

          {isAdmin && (
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase flex items-center gap-2">
                <Mail className="w-3 h-3" /> Reason for cancellation (required, sent by email)
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={4}
                maxLength={2000}
                placeholder="Explain why you are cancelling — this text will be sent to the customer in the email."
                className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-rose-500 outline-none"
                data-testid="cancel-reason-input"
              />
              <p className="text-[10px] text-slate-400 mt-1">{reason.length}/2000</p>
            </div>
          )}

          {err && (
            <div className="px-3 py-2 bg-rose-50 border border-rose-200 rounded-lg text-rose-800 text-xs flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{err}</span>
            </div>
          )}
        </div>
        <div className="flex gap-2 p-4 border-t border-slate-200">
          <button
            onClick={submit}
            disabled={submitting}
            data-testid="confirm-cancel-btn"
            className="flex-1 px-4 py-2 bg-gradient-to-r from-rose-500 to-rose-600 text-white rounded-lg font-medium disabled:opacity-50 hover:shadow-md flex items-center justify-center gap-2"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-4 h-4" />}
            {tt('cancel_modal.confirm_btn', 'Yes, cancel order')}
          </button>
          <button
            onClick={onClose}
            disabled={submitting}
            data-testid="dismiss-cancel-btn"
            className="px-4 py-2 bg-white border border-slate-300 rounded-lg font-medium hover:bg-slate-50"
          >
            {tt('cancel_modal.dismiss_btn', 'No, keep')}
          </button>
        </div>
      </div>
    </div>
  );
}
